import { create } from "zustand";
import { api } from "@/lib/api";
import type { ProviderTokens } from "@/types";

function generatePKCE(): { verifier: string; challenge: string } {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  return { verifier, challenge: "" };
}

async function computeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array)).replace(/[^a-zA-Z0-9]/g, "").slice(0, 32);
}

function isTokenExpired(tokens: ProviderTokens | undefined): boolean {
  if (!tokens || !tokens.expiresIn || !tokens.obtainedAt) return true;
  const expiresAt = tokens.obtainedAt + tokens.expiresIn * 1000;
  return Date.now() > expiresAt - 5 * 60 * 1000; // 5 minute buffer
}

interface ProviderState {
  tokens: Record<string, ProviderTokens>;
  isConnecting: boolean;
  connectProvider: (provider: "gmail" | "outlook") => Promise<void>;
  exchangeCode: (provider: "gmail" | "outlook", code: string) => Promise<void>;
  disconnectProvider: (provider: "gmail" | "outlook") => void;
  getAccessToken: (provider: "gmail" | "outlook") => Promise<string | null>;
  isConnected: (provider: "gmail" | "outlook") => boolean;
}

const OAUTH_CONFIG = {
  gmail: {
    authUrl: "https://accounts.google.com/o/oauth2/v2/auth",
    scope: "https://www.googleapis.com/auth/gmail.readonly",
    clientId: import.meta.env.VITE_GMAIL_CLIENT_ID || "",
  },
  outlook: {
    authUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    scope: "Mail.Read offline_access openid profile",
    clientId: import.meta.env.VITE_OUTLOOK_CLIENT_ID || "",
  },
};

const REDIRECT_URI = `${window.location.origin}/oauth/callback`;

export const useProviderStore = create<ProviderState>((set, get) => ({
  tokens: {},
  isConnecting: false,

  connectProvider: async (provider) => {
    const config = OAUTH_CONFIG[provider];
    if (!config.clientId) {
      throw new Error(`${provider} client ID not configured`);
    }

    const { verifier } = generatePKCE();
    const challenge = await computeChallenge(verifier);
    const state = generateState();

    sessionStorage.setItem(`pkce_${provider}_${state}`, verifier);

    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: REDIRECT_URI,
      response_type: "code",
      scope: config.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });

    if (provider === "gmail") {
      params.set("access_type", "offline");
      params.set("prompt", "consent");
    }

    window.location.href = `${config.authUrl}?${params.toString()}`;
  },

  exchangeCode: async (provider, code) => {
    set({ isConnecting: true });
    try {
      const url = new URL(window.location.href);
      const state = url.searchParams.get("state");
      if (!state) throw new Error("Missing state parameter");

      const verifier = sessionStorage.getItem(`pkce_${provider}_${state}`);
      sessionStorage.removeItem(`pkce_${provider}_${state}`);
      if (!verifier) throw new Error("PKCE verifier not found");

      const data = await api.oauthExchange(provider, code, verifier, REDIRECT_URI);
      const tokens: ProviderTokens = {
        accessToken: data.tokens.accessToken,
        refreshToken: data.tokens.refreshToken,
        expiresIn: data.tokens.expiresIn,
        obtainedAt: Date.now(),
      };

      set((s) => ({ tokens: { ...s.tokens, [provider]: tokens }, isConnecting: false }));
    } catch (err) {
      set({ isConnecting: false });
      throw err;
    }
  },

  disconnectProvider: (provider) => {
    set((s) => {
      const next = { ...s.tokens };
      delete next[provider];
      return { tokens: next };
    });
  },

  getAccessToken: async (provider) => {
    const tokens = get().tokens[provider];
    if (!tokens?.accessToken) return null;

    if (!isTokenExpired(tokens)) {
      return tokens.accessToken;
    }

    // Token expired — try to refresh
    if (!tokens.refreshToken) {
      // No refresh token available — force reconnect
      set((s) => {
        const next = { ...s.tokens };
        delete next[provider];
        return { tokens: next };
      });
      return null;
    }

    try {
      const data = await api.oauthRefresh(provider, tokens.refreshToken);
      const updated: ProviderTokens = {
        ...tokens,
        accessToken: data.tokens.accessToken,
        expiresIn: data.tokens.expiresIn,
        obtainedAt: Date.now(),
      };
      set((s) => ({ tokens: { ...s.tokens, [provider]: updated } }));
      return updated.accessToken;
    } catch {
      // Refresh failed — force reconnect
      set((s) => {
        const next = { ...s.tokens };
        delete next[provider];
        return { tokens: next };
      });
      return null;
    }
  },

  isConnected: (provider) => {
    return !!get().tokens[provider]?.accessToken;
  },
}));
